// Per-game localStorage-backed progress, onboarding flags, and arbitrary
// settings. Each `createProgressStore(gameId)` returns a small bundle of
// methods that read/write three keys under the `<gameId>.` namespace:
//
//   <gameId>.progress.v1   { [levelId]: LevelProgress }
//   <gameId>.flags.v1      { [flagKey]: boolean }
//   <gameId>.settings.v1   { [settingKey]: unknown }
//
// All methods swallow QuotaExceeded / SecurityError / SSR `window` access so
// games can call them unconditionally — persistence simply degrades to in-
// memory for that session.

export interface LevelProgress {
  completed: boolean;
  /** Game-defined "best score" metric (mirrors used, deaths, etc). Lower is better. */
  bestMetric?: number;
  /** Best clear time in milliseconds. */
  bestTimeMs?: number;
  /** Catch-all bag for additional per-game stats. */
  metrics?: Record<string, number>;
}

export interface MarkCompletePayload {
  bestMetric?: number;
  bestTimeMs?: number;
  metrics?: Record<string, number>;
}

export type ProgressMap = Record<string, LevelProgress>;

export interface ProgressStore {
  /** Returns a snapshot of all completed levels for this game. */
  getAll(): ProgressMap;
  /** Returns the stored entry for `levelId`, or `undefined` if never completed. */
  get(levelId: string): LevelProgress | undefined;
  /** Convenience: true iff `levelId` has an entry with `completed: true`. */
  isComplete(levelId: string): boolean;
  /**
   * Marks `levelId` complete and updates "best" metrics if the new payload
   * improves on the stored ones. `bestMetric` is treated as "lower is better".
   */
  markComplete(levelId: string, payload?: MarkCompletePayload): void;

  /** Returns true if `key` has been set to true via setFlag. */
  flag(key: string): boolean;
  /** Sets `key` to true (or removes it if `value` is false). */
  setFlag(key: string, value: boolean): void;

  /** Reads `key` from the settings blob, returning `fallback` if missing. */
  setting<T>(key: string, fallback: T): T;
  /** Writes `key` into the settings blob. */
  setSetting<T>(key: string, value: T): void;

  /** Removes every key this store manages. Intended for tests + a "reset progress" button. */
  clear(): void;
}

interface StoreKeys {
  progress: string;
  flags: string;
  settings: string;
}

export function createProgressStore(gameId: string): ProgressStore {
  if (!/^[a-z][a-z0-9-]*$/.test(gameId)) {
    throw new Error(`createProgressStore: invalid gameId "${gameId}"`);
  }
  const keys: StoreKeys = {
    progress: `${gameId}.progress.v1`,
    flags: `${gameId}.flags.v1`,
    settings: `${gameId}.settings.v1`,
  };

  return {
    getAll: () => readObject<ProgressMap>(keys.progress) ?? {},
    get: (levelId) => readObject<ProgressMap>(keys.progress)?.[levelId],
    isComplete: (levelId) => readObject<ProgressMap>(keys.progress)?.[levelId]?.completed === true,

    markComplete: (levelId, payload) => {
      const map = readObject<ProgressMap>(keys.progress) ?? {};
      const prev = map[levelId];
      const next: LevelProgress = { completed: true };

      const incomingMetric = payload?.bestMetric;
      const prevMetric = prev?.bestMetric;
      const better =
        incomingMetric === undefined
          ? false
          : prevMetric === undefined || incomingMetric < prevMetric;

      const bestMetric = better ? incomingMetric : prevMetric;
      const bestTimeMs = better ? payload?.bestTimeMs : prev?.bestTimeMs;
      const metrics = better ? payload?.metrics : prev?.metrics;

      if (bestMetric !== undefined) next.bestMetric = bestMetric;
      if (bestTimeMs !== undefined) next.bestTimeMs = bestTimeMs;
      if (metrics !== undefined) next.metrics = metrics;

      map[levelId] = next;
      writeObject(keys.progress, map);
    },

    flag: (key) => readObject<Record<string, boolean>>(keys.flags)?.[key] === true,
    setFlag: (key, value) => {
      const map = readObject<Record<string, boolean>>(keys.flags) ?? {};
      if (value) map[key] = true;
      else delete map[key];
      writeObject(keys.flags, map);
    },

    setting: <T>(key: string, fallback: T): T => {
      const blob = readObject<Record<string, unknown>>(keys.settings);
      if (!blob || !(key in blob)) return fallback;
      return blob[key] as T;
    },
    setSetting: <T>(key: string, value: T): void => {
      const blob = readObject<Record<string, unknown>>(keys.settings) ?? {};
      blob[key] = value;
      writeObject(keys.settings, blob);
    },

    clear: () => {
      removeKey(keys.progress);
      removeKey(keys.flags);
      removeKey(keys.settings);
    },
  };
}

function readObject<T>(key: string): T | null {
  try {
    if (typeof window === 'undefined') return null;
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function writeObject(key: string, value: unknown): void {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // localStorage unavailable; persistence degrades to in-memory.
  }
}

function removeKey(key: string): void {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.removeItem(key);
  } catch {
    // localStorage unavailable; nothing to remove.
  }
}
